import React, { useState, ChangeEvent, FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import onboardingImage from '../../../assets/images/onboarding.webp';
import { LoginBody } from '../../../interfaces/core/lmsCore';
import { login } from './actions';

/**
 * Onboarding component for the LMS platform.
 *
 * This component renders the onboarding form with fields for username and password.
 * It includes validation with inline error messages and a fade-in animation for errors.
 * On successful initialization, the user is navigated to the courses page.
 *
 * @returns {JSX.Element} The rendered onboarding component.
 */
const Onboarding: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [credentials, setCredentials] = useState<LoginBody>({
    username: '',
    password: '',
    regid: process.env.REACT_APP_REGID || 'x',
    source: process.env.REACT_APP_SOURCE || 'ios',
  });
  const [errors, setErrors] = useState<any>({});

  /**
   * Handles the change event for input fields.
   *
   * Updates the corresponding credential state and clears any existing error for that field.
   *
   * @param {ChangeEvent<HTMLInputElement>} e - The change event from the input field.
   */
  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setCredentials((prev) => ({ ...prev, [name]: value }));
    // Remove error for the specific field when input changes
    setErrors((prev: any) => ({ ...prev, [name]: undefined }));
  };

  /**
   * Handles the form submission event.
   *
   * Calls the login action to validate credentials and initialize the LMS.
   * On success, navigates to /courses.
   *
   * @param {FormEvent<HTMLFormElement>} e - The form submission event.
   * @returns {Promise<void>} A promise that resolves when form handling is complete.
   */
  const handleSubmit = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    
    const result = await login(credentials, t);
    
    if (result) {
      navigate('/courses');
    }
  };

  return (
    <div className="flex min-h-screen w-full">
      {/* Left side */}
      <div
        className="w-1/2 md:hidden relative flex flex-col justify-center items-center overflow-hidden bg-cover bg-center mt-2 ml-2 mb-2 rounded-xl"
        style={{ backgroundImage: `url(${onboardingImage})` }}
      >
        <div className="absolute inset-0 bg-black opacity-50"></div>
        <div className="z-10 text-white px-12 text-left">
          <h1 className="text-5xl font-bold mb-2">{t('app.name')}</h1>
          <p className="text-lg mb-6">{t('onboarding.tagline')}</p>
        </div>
        {/* Testimonial bubbles */}
        <div className="absolute top-10 left-10 z-20 bg-white/30 backdrop-blur-md text-white px-6 py-4 rounded-2xl shadow-xl max-w-xs">
          {t('onboarding.testimonial1')}
        </div>
        <div className="absolute bottom-20 right-10 z-20 bg-white/30 backdrop-blur-md text-white px-6 py-4 rounded-2xl shadow-xl max-w-xs">
          {t('onboarding.testimonial2')}
        </div>
      </div>

      {/* Right side - onboarding form */}
      <div className="w-1/2 md:w-full flex items-center justify-center px-16">
        <div className="max-w-md w-full">
          <div className="mb-12">
            <h2 className="text-3xl font-bold text-gray-800">{t('onboarding.getStarted')}</h2>
            <p className="text-gray-600 mt-1">{t('onboarding.onboardingDescription')}</p>
          </div>

          <form onSubmit={handleSubmit}>
            {/* Username Input */}
            <div className="mb-1">
              <div
                className={`flex items-center border ${errors.username ? 'border-red-500' : 'border-gray-200'} rounded-lg px-4 py-3`}
              >
                <input
                  type="text"
                  name="username"
                  placeholder={t('onboarding.username')}
                  value={credentials.username}
                  onChange={handleChange}
                  className="w-full outline-none bg-transparent text-gray-600"
                />
              </div>
              <div className="h-5 mt-1">
                <p
                  className={`text-red-500 text-sm transition-opacity duration-300 ${errors.username ? 'opacity-100' : 'opacity-0'}`}
                >
                  {errors.username || ' '}
                </p>
              </div>
            </div>

            {/* Password Input */}
            <div className="mb-2">
              <div
                className={`flex items-center border ${errors.password ? 'border-red-500' : 'border-gray-200'} rounded-lg px-4 py-3`}
              >
                <input
                  type="password"
                  name="password"
                  placeholder={t('onboarding.password')}
                  value={credentials.password}
                  onChange={handleChange}
                  className="w-full outline-none bg-transparent text-gray-600"
                />
              </div>
              <div className="h-5 mt-1">
                <p
                  className={`text-red-500 text-sm transition-opacity duration-300 ${errors.password ? 'opacity-100' : 'opacity-0'}`}
                >
                  {errors.password || ' '}
                </p>
              </div>
            </div>

            <button
              type="submit"
              className="w-full bg-primary hover:bg-secondary text-white font-medium py-3 rounded-lg transition"
            >
              {t('onboarding.continue')}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Onboarding;
